import { deepmerge } from '@mui/utils';
import React, { FC, PropsWithChildren, createContext, useCallback, useContext } from 'react';
import bdsMessages from './bds.intl.json';
import { useDeepMemo } from '../utils/useDeepMemo';
import { Utilities } from '../types';

export type Messages = typeof bdsMessages;

export type BdsMessages = Utilities.FirstDepthOptionalDeeperRequired<Messages>;

export type IntlBdsContextProps = {
  bdsMessages: Messages;
  format: (message: string, values: Record<string, string | number>) => string;
};

export const IntlBdsContext = createContext<IntlBdsContextProps>({
  bdsMessages,
  /**
   * Simple formatting function that can be replaced by an application
   * or used in tests where the formatting function is not injected into BdsIntlProvider.
   */
  format: (message: string, values: Record<string, string | number>) => {
    return message.replace(/{([^{}]*)}/g, (match, key) => (values[key] ? `${values[key]}` : match));
  }
});

export interface BdsIntlProps {
  messages: BdsMessages | undefined;
  format: ((message: string, values: Record<string, string | number>) => string) | undefined;
}

export const BdsIntlProvider: FC<PropsWithChildren<BdsIntlProps>> = ({ children, messages, format }) => {
  /**
   * Formats a message by replacing placeholders with corresponding values.
   *
   * const message = "Hello, {name}!";
   * const values = { name: "Alice" };
   * const formattedMessage = BdsFormat(message, values);
   * console.log(formattedMessage); // "Hello, Alice!"
   *
   */
  const BdsFormat = useCallback(
    (message: string, values: Record<string, string | number>) =>
      message.replace(/{([^{}]*)}/g, (match, key) => (values[key] ? `${values[key]}` : match)),
    []
  );

  const value = useDeepMemo(
    () => ({ bdsMessages: deepmerge(bdsMessages, messages), format: format || BdsFormat }),
    [messages, format, BdsFormat]
  );

  return <IntlBdsContext.Provider value={value}>{children}</IntlBdsContext.Provider>;
};

export function useIntlBds() {
  const { bdsMessages, format } = useContext<IntlBdsContextProps>(IntlBdsContext);

  return { intl: bdsMessages, format };
}
